/* Copyright (c) 2020-present CloudIO Inc */

import { attachmentProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './AttachmentField';

const AttachmentFieldManager: ComponentManagerFn<'AttachmentField'> = (
  platform: Platform,
  type: 'AttachmentField',
): ComponentManager<'AttachmentField'> => {
  return {
    ...baseComponentManager<'AttachmentField'>(platform, 'AttachmentField'),
    type: 'AttachmentField',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        ...baseFieldProps.viewAttribute,
        getAttributeDataTypes: () => ['Attachment'],
      } as ViewAttributeChooserProp,
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      postAPI: {
        label: 'Post to API',
        type: 'boolean',
        helpText: 'Check this to post the file to an API',
      },
      apiURL: {
        label: 'API URL',
        type: 'string',
      },
      apiHeaders: {
        label: 'JSON Data',
        type: 'json',
      },
      helperText: {
        label: 'Help Text',
        type: 'string',
      },
      ...attachmentProps,
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'accept': {
          return !itemData.images;
        }
        case 'colorExpression':
        case 'bgColorExpression':
          return false;
        case 'imageHeight':
        case 'showImage':
        case 'maxImageWidth': {
          return itemData.images;
        }
        case 'apiHeaders':
        case 'apiURL': {
          return itemData.postAPI;
        }
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    name: 'Attachment Field',
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'paperclip' },
    description: `**Overview**<br/>
The Attachment Field is a form component that enables users to upload files to a record.
It renders as a file picker (often a "Browse" button or drag-and-drop zone) and handles the complexities of file storage, MIME types, and display previews. It is used for attaching documents (PDFs, Docx) or media (Images) to specific database records.

**1. Data Binding**<br/>
Unlike text fields, this component requires a specific attribute type to handle binary data and file metadata.
* **View Attribute:** Select the attribute in your Data Store where the file reference will be saved.
* **Requirement:** The attribute must be of type **Attachment**. You cannot bind this to a standard String field.

**2. Display Modes (Document vs. Image)**<br/>
You can configure the field to handle generic files or strictly optimize it for photos.
* **Images (Toggle):**
    * **Enabled (True):** The field treats uploads as images. (**Show Image** renders a thumbnail preview; **Image Height / Max Width** controls dimensions).
    * **Disabled (False):** The field treats uploads as generic files. (**Accept** defines allowed file types e.g., .pdf,.docx; Visuals render the filename and icon).

**3. Direct API Upload (Advanced)**<br/>
By default, the file is saved to the configured storage service when the Form/Data Store is saved. However, you can configure the field to upload the file to an external API immediately upon selection.
* **Post to API:**
    * **Enabled:** The file is not stored in the default object store; instead, it is POSTed to the URL defined below.
* **API URL:** The endpoint where the file should be sent (e.g., a custom AWS Lambda or third-party document service).
* **JSON Data (Headers):** A JSON object containing any headers required for the request (e.g., \`{"Authorization": "Bearer ...", "x-custom-header": "value"}\`).

**4. Interaction & Logic**<br/>
File uploads often trigger downstream processes (validation, metadata extraction, or immediate saving).
* **TriggerOnChange:** You can add this child component to execute logic immediately after a file is successfully selected/uploaded.
* **Accessing File Context:** Inside your trigger logic, you can access specific metadata about the uploaded file using the following expressions:
    * \`$._.file.fileName\` (e.g., "contract.pdf")
    * \`$._.file.fileSize\` (Size in bytes)
    * \`$._.file.contentType\` (MIME type, e.g., application/pdf)
    * \`$._.file.fileUid\` (Unique ID of the file)
    * \`$._.file.fileVersion\` (Version number if versioning is enabled)`,
  };
};

export default AttachmentFieldManager;
